import React, { FC, MouseEvent, MouseEventHandler, useCallback, useMemo } from 'react'

import { Language } from '~generated/types'
import GrammarIcon from '~icons/grammar.svg'
import LanguageIcon from '~icons/language.svg'
import LaunchBriefIcon from '~icons/launchBrief-md.svg'
import WriteIcon from '~icons/write.svg'
import Menu, { Submenu } from '~lib/ui-kit/Menu'
import MenuLayout, { MenuVariant } from '~lib/ui-kit/MenuLayout'
import MenuList from '~lib/ui-kit/MenuList/MenuList'
import useContinueWriting from '~modules/aiAssist/usecases/useContinueWriting'
import useCorrectGrammar from '~modules/aiAssist/usecases/useCorrectGrammar'
import useSummarize from '~modules/aiAssist/usecases/useSummarize'
import useTranslateText from '~modules/aiAssist/usecases/useTranslateText'

interface AiAssistMenuProps {
  className?: string
  text: () => string
  onStart?: () => void
  onComplete: (text: string) => void
  onError?: (error: Error) => void
}

type MenuItemOptionType = {
  label: string
  value: string
  onClick: MouseEventHandler<HTMLDivElement>
}

type MenuItemType = {
  icon: JSX.Element
  label: string
  onClick?: MouseEventHandler<HTMLDivElement>
  options?: MenuItemOptionType[]
  // TODO hint?: string
}

const AiAssistMenu: FC<React.PropsWithChildren<AiAssistMenuProps>> = ({
  text,
  onStart,
  onComplete,
  onError,
  className,
}) => {
  const curriedGenerateAndInsertContent = useCallback(
    (cb: (text: string, ev: MouseEvent) => Promise<string>) => async (ev: MouseEvent) => {
      try {
        const inputText = text instanceof Function ? text() : text

        const sanitizedText = inputText?.trim()

        if (!sanitizedText) return

        onStart?.()
        const generatedText = await cb(sanitizedText, ev)
        onComplete(generatedText)
      } catch (error) {
        onError?.(error)
      }
    },
    [onComplete, onError, onStart, text]
  )

  const { continueWriting } = useContinueWriting()
  const { summarize } = useSummarize()
  const { correctGrammar } = useCorrectGrammar()
  const { languages, translate } = useTranslateText()

  const handleTranslate = useCallback(
    (selectedText: string, ev: MouseEvent<HTMLLIElement>) => {
      const { currentTarget } = ev
      const { value } = currentTarget.dataset

      return translate({ text: selectedText, language: value as Language })
    },
    [translate]
  )

  const menuItems: MenuItemType[] = useMemo(() => {
    const handleTranslateClick = curriedGenerateAndInsertContent(handleTranslate)

    return [
      {
        icon: <WriteIcon />,
        label: 'Continue writing',
        onClick: curriedGenerateAndInsertContent(continueWriting),
      },
      {
        icon: <LaunchBriefIcon />,
        label: 'Summarize',
        onClick: curriedGenerateAndInsertContent(summarize),
      },
      {
        icon: <GrammarIcon />,
        label: 'Check grammar',
        onClick: curriedGenerateAndInsertContent(correctGrammar),
      },
      {
        icon: <LanguageIcon />,
        label: 'Translate',
        options: languages.map((lang) => ({ ...lang, onClick: handleTranslateClick })),
      },
    ]
  }, [curriedGenerateAndInsertContent, handleTranslate, continueWriting, summarize, correctGrammar, languages])

  return (
    <MenuLayout className={className} variant={MenuVariant.CONTEXT}>
      <MenuList>
        {menuItems.map(({ icon, label, onClick, options }) =>
          options ? (
            <Submenu
              variant={MenuVariant.CONTEXT}
              key={label}
              trigger="hover"
              label={label}
              placement="right-start"
              placeholder="Search..."
              onClick={onClick}
              icon={icon}
              searchable
            >
              {options.map(({ label, value, onClick: onSubmenuItemClick }) => (
                <Menu.Item key={value} data-value={value} onClick={onSubmenuItemClick}>
                  {label}
                </Menu.Item>
              ))}
            </Submenu>
          ) : (
            <Menu.Item key={label} icon={icon} onClick={onClick}>
              {label}
            </Menu.Item>
          )
        )}
      </MenuList>
    </MenuLayout>
  )
}

export default AiAssistMenu
